import { Component, inject, signal } from '@angular/core';
import { SettingsSectionComponent } from 'src/app/components/settings-section/settings-section.component';
import { SettingsHeaderComponent } from 'src/app/components/settings-header/settings-header.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthStore } from 'src/app/stores/auth.store';
import { FormatDateUtils } from 'src/app/utils/format-date-utils';
import { ModalsService } from 'src/app/services/modals.service';
import { RegisterApiModalComponent } from 'src/app/components/modals/register-api-modal/register-api-modal.component';
import { tap, catchError, EMPTY } from 'rxjs';
import { ApiKeyManagementService } from 'src/app/services/api-key-management-service';
import { RevokeApiModalComponent } from 'src/app/components/modals/revoke-api-modal/revoke-api-modal.component';
import { environment } from 'src/environments/environment';
import { openLinkInANewTab } from 'src/app/utils/open-in-a-new-tab.utils';
import { ApiScope, scopes } from 'src/app/utils/get-api-scopes.utils';
import { ToastrFactoryService } from 'src/app/services/toastr-factory.service';
import { TourService } from 'src/app/services/tour/tour.service';

interface ApiKey {
  id: number;
  type: string;
  key: string | undefined;
  status: string;
  createdDate: string;
  isExpanded: boolean;
  scopes: ApiScope[];
}

@Component({
  selector: 'app-settings-api-key',
  imports: [TranslateModule, SettingsSectionComponent, SettingsHeaderComponent],
  templateUrl: './api-management.component.html',
  styleUrl: './api-management.component.scss',
})
export class ApiManagementComponent {
  protected authStore = inject(AuthStore);
  private translateService = inject(TranslateService);
  private toastrFactory = inject(ToastrFactoryService);
  protected modalsService = inject(ModalsService);
  protected apiKeyService = inject(ApiKeyManagementService);
  protected tourService = inject(TourService);
  expandedItem: any = null;
  scopes = signal<ApiScope[]>(scopes);

  protected tableHeaders = ['', 'API.type', 'API.apiKeyLast5Characters', 'API.status', 'API.details', 'API.action'];

  demoApiKeys: ApiKey[] = [
    {
      id: 1,
      type: 'integrator',
      key: this.getApiKey(),
      status: 'API.active',
      createdDate: this.getDate(),
      isExpanded: false,
      scopes: scopes,
    },
  ];

  public startApiTour() {
    this.tourService.startTour('SETTINGS.API_MANAGEMENT');
  }

  getApiKey() {
    return this.authStore.profile()?.apiKey?.replace(/[^a-zA-Z0-9]/g, '');
  }

  getBorderRadius(index: number) {
    return index === this.tableHeaders.length - 1 ? 'rounded-tr-lg' : '';
  }

  getDate() {
    const lang = this.translateService.currentLang || 'en-US';
    return FormatDateUtils.formatDate(this.authStore.profile()?.apiKeyCreatedDate, lang);
  }

  getHeaderClass(index: number): string {
    return `py-3 text-left text-xs font-medium text-gray-500
      ${index === 0 ? 'rounded-l-lg' : ''}
      ${index === this.tableHeaders.length - 1 ? 'rounded-r-lg' : ''}`;
  }

  getExpandIconClass(isExpanded: boolean): string {
    return isExpanded ? 'ph-caret-up' : 'ph-caret-down';
  }

  toggleRow(event: Event, id: number) {
    const apiKey = this.demoApiKeys.find((key) => key.id === id);
    if (apiKey) {
      apiKey.isExpanded = !apiKey.isExpanded;
    }
  }

  getApiLink() {
    return environment.apiPublic;
  }

  getSubtitle() {
    return `${this.translateService.instant('API.generatedApiKeysInfo')} <br>
       ${this.translateService.instant('API.clickHereToAccessTheApiDocumentation', { apiLink: this.getApiLink() })}
     `;
  }

  protected async generateApiKey(): Promise<void> {
    // Example of how to set the width of the modal
    const res = await this.modalsService.openModal(RegisterApiModalComponent);
    if (res) this.authStore.currentUser();
  }

  protected async revokeApiKey(): Promise<void> {
    const res = await this.modalsService.openModal(RevokeApiModalComponent, { data: { apiKey: this.getApiKey() } });
    if (!res) return;
    this.apiKeyService
      .deleteApiKey()
      .pipe(
        tap(async (response: any) => {
          this.toastrFactory.success(this.translateService.instant('apiKeySuccessfullyRevoked'));
          this.authStore.currentUser();
        }),
        catchError((error: any) => {
          this.toastrFactory.unknownError(this.translateService.instant('unknownError'));
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  redirectToApiDocs(docId: string) {
    const url = `${environment.apiPublic}#${docId}`;
    openLinkInANewTab(url);
  }
}
